import { SurveyFeedback } from '@/client/security-culture/types';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import {
  ChartEmptyState,
  ChartEmptyStateType,
} from '@/components/dashboards/admin';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { ChartIcon } from '@/utils/helpers';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { CardInfo } from './CardInfo';

const StyledCardContainer = styled(FlexContainer)<{ isDemo?: boolean }>`
  background-color: ${({ isDemo }) =>
    isDemo ? 'var(--purple-background)' : 'var(--beige-main)'};
  padding: var(--default-padding) var(--big-padding);
  border-radius: var(--medium-border-radius);
  margin: 0;
  overflow: hidden;

  &.stretch-children {
    .children {
      overflow-y: hidden;
      overflow-x: auto;
      padding-bottom: var(--medium-padding);

      > div {
        flex: 1;

        > div {
          width: 100%;
        }
      }
    }
  }

  .demo-overlay {
    position: relative;
    ${({ isDemo }) =>
      isDemo &&
      `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: var(--purple-background);
      opacity: 0.5;
      z-index: 1;
    }
  `}
  }
`;

const StyledFeedbackContainer = styled(FlexContainer)`
  width: 100%;
  border-bottom: 1px solid var(--gray-dark);
  padding: var(--xsmall-padding) 0;
  gap: var(--medium-padding);

  &:last-child {
    border-bottom: none;
  }

  svg {
    flex-shrink: 0;
  }
`;

type SCFeedbackProps = {
  feedback: SurveyFeedback[] | undefined;
  surveyId?: string;
  isDemo?: boolean;
  isFilterApplied?: boolean;
};

export const SCFeedback: React.FC<SCFeedbackProps> = ({
  feedback,
  surveyId,
  isDemo,
  isFilterApplied,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <StyledCardContainer
      id="sc-feedback-card"
      direction="column"
      gap={24}
      justify="flex-start"
      flex="0.7"
      isDemo={isDemo}
    >
      <CardInfo
        id="security-culture-feedback"
        title={t('security.culture.feedback.from.employees')}
        tooltip={
          isFilterApplied
            ? t('security.culture.feedback.filters.warning')
            : t('security.culture.feedback.from.employees.tooltip')
        }
        redTitle={isDemo ? t('security.culture.notMeasured') : undefined}
      />
      <FlexContainer
        direction="column"
        className="children demo-overlay"
        height="100%"
      >
        {feedback?.length ? (
          feedback.map((item: SurveyFeedback) => (
            <StyledFeedbackContainer justify="flex-start" key={item.id}>
              <ChartIcon />
              <p>{item.description}</p>
            </StyledFeedbackContainer>
          ))
        ) : (
          <ChartEmptyState
            type={ChartEmptyStateType.SC}
            noBorder
            noMargin
            transparent
            minHeight={200}
            title={t('security.culture.feedback.empty.title')}
            description={t('security.culture.feedback.empty.description')}
          />
        )}
      </FlexContainer>
      {!!feedback?.length && !isDemo && (
        <FlexContainer direction="row" justify="flex-end" className="mt-auto">
          <AppButton
            type="outlined"
            label={t('button.view.all')}
            onClick={() =>
              surveyId &&
              navigate(
                RedirectPaths[RedirectPathsEnum.SECURITY_CULTURE_FEEDBACK](
                  surveyId,
                ),
              )
            }
          />
        </FlexContainer>
      )}
    </StyledCardContainer>
  );
};
